import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Copy, Trash2, QrCode, Link2, Clock, Users, Shapes } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOKING_BASE = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/book`;

export const BookingLinksDialog = ({ open, onOpenChange }) => {
  const [links, setLinks] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientList, setShowPatientList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ patient_id: '', patient_name: '', expires_days: 7, doctor_id: '', format_id: '' });
  const [showQR, setShowQR] = useState(null);
  const [userRole, setUserRole] = useState('doctor');
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [meetingFormats, setMeetingFormats] = useState([]);
  const [formatsLoading, setFormatsLoading] = useState(false);

  const fetchMeetingFormats = useCallback(async (doctorId = '', role = userRole) => {
    setFormatsLoading(true);
    try {
      const isClinicManager = role === 'admin' || role === 'owner';
      if (isClinicManager && !doctorId) {
        setMeetingFormats([]);
        return;
      }
      const { data } = isClinicManager
        ? await api.get(`/team/doctors/${doctorId}/schedule`)
        : await api.get('/settings');
      setMeetingFormats(Array.isArray(data.meeting_formats) ? data.meeting_formats : []);
    } catch {
      setMeetingFormats([]);
      toast.error('Не удалось загрузить форматы сеансов');
    } finally {
      setFormatsLoading(false);
    }
  }, [userRole]);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const [linksRes, patientsRes, meRes] = await Promise.all([
        api.get(`/booking-links`),
        api.get(`/patients`),
        api.get(`/auth/me`),
      ]);
      setLinks(linksRes.data);
      setPatients(patientsRes.data);
      const role = meRes.data.role || 'doctor';
      setUserRole(role);
      if (role === 'admin' || role === 'owner') {
        api.get('/team/doctors').then(({ data }) => setClinicDoctors(Array.isArray(data) ? data : [])).catch(() => {});
        setForm((current) => ({ ...current, doctor_id: '', format_id: '' }));
        setMeetingFormats([]);
      } else {
        fetchMeetingFormats('', role);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [fetchMeetingFormats]);

  useEffect(() => {
    if (open) fetchLinks();
  }, [open, fetchLinks]);

  const selectPatient = (p) => {
    setForm({ ...form, patient_id: p.id, patient_name: p.name });
    setPatientSearch('');
    setShowPatientList(false);
  };

  const selectDoctor = async (doctorId) => {
    setForm((current) => ({ ...current, doctor_id: doctorId, format_id: '' }));
    await fetchMeetingFormats(doctorId, userRole);
  };

  const filteredPatients = patientSearch.trim()
    ? patients.filter((p) =>
        p.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
        (p.phone || '').includes(patientSearch) ||
        (p.email || '').toLowerCase().includes(patientSearch.toLowerCase())
      ).slice(0, 5)
    : [];

  const createLink = async () => {
    if (!form.patient_name.trim()) { toast.error('Введите имя клиента'); return; }
    if ((userRole === 'admin' || userRole === 'owner') && !form.doctor_id) { toast.error('Выберите специалиста'); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/booking-links`, {
        ...form,
        patient_phone: '',
        patient_email: '',
      });
      setLinks((prev) => [data, ...prev]);
      setForm({ patient_id: '', patient_name: '', expires_days: 7, doctor_id: form.doctor_id, format_id: form.format_id });
      toast.success('Ссылка создана');
    } catch { toast.error('Ошибка создания ссылки'); }
    setCreating(false);
  };

  const createAnonymousLink = async () => {
    if ((userRole === 'admin' || userRole === 'owner') && !form.doctor_id) { toast.error('Выберите специалиста'); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/booking-links`, {
        patient_id: '',
        patient_name: '',
        patient_phone: '',
        patient_email: '',
        expires_days: form.expires_days,
        doctor_id: form.doctor_id,
        format_id: form.format_id,
      });
      setLinks((prev) => [data, ...prev]);
      toast.success('Обезличенная ссылка создана');
    } catch { toast.error('Ошибка создания ссылки'); }
    setCreating(false);
  };

  const deleteLink = async (id) => {
    try {
      await api.delete(`/booking-links/${id}`);
      setLinks((prev) => prev.filter((l) => l.id !== id));
      toast.success('Ссылка удалена');
    } catch { toast.error('Ошибка удаления'); }
  };

  const copyLink = (id) => {
    navigator.clipboard.writeText(`${BOOKING_BASE}/${id}`);
    toast.success('Ссылка скопирована');
  };

  const getLinkStatus = (link) => {
    if (link.used) return { label: 'Использована', cls: 'bg-stone-800/50 text-stone-500 border-stone-700' };
    const expires = new Date(link.expires_at);
    if (expires < new Date()) return { label: 'Истекла', cls: 'bg-red-900/30 text-red-400 border-red-900/50' };
    return { label: 'Активна', cls: 'bg-green-900/30 text-green-400 border-green-900/50' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-stone-200 flex items-center gap-2">
            <Link2 size={18} className="text-amber-500" /> Ссылки для записи
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs leading-relaxed text-stone-400">
          Создайте ссылку, скопируйте её и отправьте клиенту. Email для кода клиент введёт сам на странице записи. После одной записи ссылка перестанет работать.
        </p>

        {/* Create form */}
        <div className="space-y-3 p-4 rounded-lg bg-stone-950/40 border border-stone-800/50">
          <div>
            <p className="text-sm text-stone-300 font-medium">Персональная ссылка</p>
            <p className="text-xs text-stone-600 mt-0.5">Выберите клиента из базы или укажите имя. Почту клиента знать заранее не нужно.</p>
          </div>

          {/* Doctor selector (clinic managers only) */}
          {(userRole === 'admin' || userRole === 'owner') && clinicDoctors.length > 0 && (
            <div>
              <label className="text-stone-500 text-[10px] mb-1 block">Специалист *</label>
              <select value={form.doctor_id} onChange={e => selectDoctor(e.target.value)}
                className="w-full rounded-md bg-stone-950/50 border border-stone-800 text-stone-200 p-2 text-sm">
                <option value="">Выберите специалиста...</option>
                {clinicDoctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="booking-link-format" className="text-stone-500 text-[10px] mb-1 block">
              Формат сеанса
            </label>
            <div className="flex items-center gap-2">
              <Shapes size={14} className="text-stone-600 shrink-0" aria-hidden="true" />
              <select
                id="booking-link-format"
                data-testid="link-format-id"
                value={form.format_id}
                onChange={(event) => setForm({ ...form, format_id: event.target.value })}
                disabled={formatsLoading || ((userRole === 'admin' || userRole === 'owner') && !form.doctor_id)}
                className="w-full rounded-md bg-stone-950/50 border border-stone-800 text-stone-200 p-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">Клиент выберет при записи</option>
                {meetingFormats.map((formatItem) => (
                  <option key={formatItem.id} value={formatItem.id}>
                    {formatItem.name} · {formatItem.duration} мин.
                  </option>
                ))}
              </select>
            </div>
            <p className="text-stone-600 text-[10px] leading-relaxed mt-1">
              Если выбрать формат, по ссылке нельзя будет записаться на другой.
            </p>
          </div>

          {/* Patient search */}
          {patients.length > 0 && (
            <div className="relative">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-stone-600 shrink-0" />
                <Input
                  data-testid="patient-search-input"
                  placeholder="Найти клиента..."
                  value={patientSearch}
                  onChange={(e) => { setPatientSearch(e.target.value); setShowPatientList(true); }}
                  onFocus={() => setShowPatientList(true)}
                  className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600 text-sm h-8"
                />
              </div>
              {showPatientList && filteredPatients.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-stone-800 border border-stone-700 rounded-lg shadow-xl overflow-hidden">
                  {filteredPatients.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`select-patient-${p.id}`}
                      onClick={() => selectPatient(p)}
                      className="w-full px-3 py-2 text-left hover:bg-stone-700 transition-colors"
                    >
                      <p className="text-sm text-stone-200">{p.name}</p>
                      <p className="text-xs text-stone-500">{p.email || p.phone || 'Нет контактов'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-stone-500 text-[10px] mb-1 block">Имя или обозначение клиента *</label>
            <Input data-testid="link-patient-name" placeholder="Например, Анна" value={form.patient_name}
              onChange={(e) => setForm({ ...form, patient_id: '', patient_name: e.target.value })}
              className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600 text-sm" />
            <p className="text-stone-600 text-[10px] leading-relaxed mt-1">
              Если клиент выбран из базы, ссылка останется привязана к его карточке без передачи email.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-stone-500 text-xs whitespace-nowrap">Срок (дней):</label>
            <Input
              data-testid="link-expires-days"
              type="number"
              min={1}
              max={90}
              value={form.expires_days}
              onChange={(e) => setForm({ ...form, expires_days: parseInt(e.target.value) || 7 })}
              className="w-20 bg-stone-950/50 border-stone-800 text-stone-200 text-sm"
            />
            <Button
              data-testid="create-link-btn"
              onClick={createLink}
              disabled={creating}
              className="ml-auto bg-amber-700 hover:bg-amber-600 text-white text-sm"
              size="sm"
            >
              <Plus size={14} className="mr-1" /> Создать
            </Button>
          </div>
          <Button
            data-testid="create-anonymous-link-btn"
            onClick={createAnonymousLink}
            disabled={creating}
            variant="outline"
            className="w-full border-stone-700 text-stone-400 hover:text-stone-200 hover:bg-stone-800 text-xs"
            size="sm"
          >
            <Users size={13} className="mr-1.5" /> Создать ссылку без выбранного клиента
          </Button>
          <p className="text-stone-600 text-[10px] leading-relaxed">
            Получатель сам введёт имя и email. Такая ссылка тоже одноразовая и действует указанный срок.
          </p>
        </div>

        {/* Links list */}
        <div className="space-y-2 mt-2">
          {loading ? (
            <p className="text-stone-600 text-sm text-center py-4">Загрузка...</p>
          ) : links.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-stone-400 text-sm">Ссылок пока нет</p>
              <p className="text-stone-600 text-xs mt-1">Заполните форму выше, затем отправьте клиенту скопированный адрес или QR-код.</p>
            </div>
          ) : (
            links.map((link) => {
              const status = getLinkStatus(link);
              const isActive = !link.used && new Date(link.expires_at) >= new Date();
              return (
                <div
                  key={link.id}
                  data-testid={`booking-link-${link.id}`}
                  className="p-3 rounded-lg bg-stone-950/30 border border-stone-800/50 hover:border-stone-700 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-stone-200">{link.patient_name || 'Обезличенная'}</p>
                      <Badge className={`${status.cls} text-[10px] border`}>{status.label}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      {isActive && (
                        <>
                          <Button
                            data-testid={`copy-link-${link.id}`}
                            variant="ghost" size="sm"
                            onClick={() => copyLink(link.id)}
                            aria-label="Скопировать ссылку"
                            title="Скопировать ссылку"
                            className="text-stone-500 hover:text-amber-400 h-7 w-7 p-0"
                          >
                            <Copy size={14} />
                          </Button>
                          <Button
                            data-testid={`qr-link-${link.id}`}
                            variant="ghost" size="sm"
                            onClick={() => setShowQR(showQR === link.id ? null : link.id)}
                            aria-label="Показать QR-код"
                            title="Показать QR-код"
                            className="text-stone-500 hover:text-amber-400 h-7 w-7 p-0"
                          >
                            <QrCode size={14} />
                          </Button>
                        </>
                      )}
                      <Button
                        data-testid={`delete-link-${link.id}`}
                        variant="ghost" size="sm"
                        onClick={() => deleteLink(link.id)}
                        aria-label="Удалить ссылку"
                        title="Удалить ссылку"
                        className="text-stone-600 hover:text-red-400 h-7 w-7 p-0"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-stone-500 mb-1 flex items-center gap-1.5">
                    <Shapes size={11} aria-hidden="true" />
                    {link.format_name || 'Любой доступный формат'}
                  </p>
                  <p className="text-[10px] text-stone-600 flex items-center gap-1">
                    <Clock size={10} />
                    до {new Date(link.expires_at).toLocaleDateString('ru-RU')}
                  </p>

                  {/* QR Code */}
                  {showQR === link.id && (
                    <div className="mt-3 flex flex-col items-center gap-2 p-4 rounded-lg bg-white">
                      <QRCodeSVG
                        value={`${BOOKING_BASE}/${link.id}`}
                        size={180}
                        bgColor="#ffffff"
                        fgColor="#1C1917"
                        level="M"
                      />
                      <p className="text-xs text-stone-600 text-center break-all">{`${BOOKING_BASE}/${link.id}`}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
